import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import {
  Button,
  ListBook,
  Input,
  ToggleSwitch,
  InfoAlert,
  FontIcon
} from '@arvoreeducacao/bonsai-ds'
import ReactTooltip from 'react-tooltip'
import {
  Form,
  Field,
  List,
  Item,
  IndicatedTo,
  Link,
  StyledAutoComplete,
  SwitchWrapper,
  InfoAlertWrapper
} from './ContentIndicationForm.styles'
import {
  searchBookForAutoCompleteQuery,
  bookQuery,
  listReadersWithSameDegreeQuery,
  searchAvailableReadersToIndicateQuery,
  entitiesSubtreeFlatQuery
} from 'api/queries'
import { debounce, groupBy } from 'lodash'
import locale from 'date-fns/locale/pt'
import { isPast, format, isValid } from 'date-fns'
import useToast from 'utils/hooks/use-toast'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import { useTranslation } from 'i18n'
import { useApolloClient } from '@apollo/client'
import type { Book, User } from 'api/data.types'
import { IndicationContentType, IndicationTargetType } from 'api/data.types'
import { createContentIndications } from 'api/mutations/reader'
import { customInstance } from 'lib/api-instance'
import type {
  ContentIndicationFormProps,
  ContentIndicationItem,
  State,
  Target
} from './ContentIndicationForm.types'

const ITEMS_TO_SHOW_IN_AUTOCOMPLETE = 5

function ContentIndicationForm(props: ContentIndicationFormProps) {
  const indicateToRef = useRef('')
  const dateInputRef = useRef('')
  const bookInputRef = useRef('')
  const { t } = useTranslation()
  const { user } = useUserInfo()
  const { toastMessage } = useToast()
  const client = useApolloClient()
  const isVideoIndication = props.contentType === IndicationContentType.VIDEO
  const hasActivitiesV3 = user.featureToggles?.activitiesV3
  const [showDeadline, setShowDeadline] = useState(false)
  const [state, _setState] = useState<State>({
    books: [],
    autocompleteBooks: [],
    indicatedTo: [],
    autocompleteIndicatedTo: [],
    canIndicateTo: [],
    loading: false,
    entitiesSubtreeFlat: [],
    dateIsValid: true,
    dateHelper: '',
    reader: []
  })

  const setState = useCallback((newState: Partial<typeof state>) => {
    _setState(prevState => ({
      ...prevState,
      ...newState
    }))
  }, [])

  function handleValidationDate(date: string) {
    const dateValue = new Date(`${date}T23:59:59`)

    if (isPast(dateValue)) {
      setState({
        loading: false,
        dateIsValid: false,
        dateHelper: t(
          'O campo "Ler até" deve conter uma data maior ou igual a hoje.'
        )
      })
      return false
    }

    if (!isValid(dateValue)) {
      setState({
        loading: false,
        dateIsValid: false,
        dateHelper: t(
          'O campo "Ler até" precisa ter uma data válida (exemplo: 19/03/2018)'
        )
      })
      return false
    }
    setState({
      dateIsValid: true
    })
    return true
  }

  function formatModalDate(hasDate: boolean): string | undefined {
    if (!hasDate) return

    return format(
      dateInputRef.current.split('/').reverse().join('/'),
      'YYYY-MM-DD',
      { locale }
    )
  }

  function submitIndication(
    books: ContentIndicationItem[],
    isTeacher: boolean
  ) {
    const { indicatedTo } = state
    const hasDate =
      isTeacher &&
      (hasActivitiesV3 ? showDeadline : true) &&
      !!dateInputRef.current?.length

    if (hasDate && !handleValidationDate(dateInputRef.current)) return

    const indications = books.map(book => {
      const indicateTo = isTeacher
        ? groupBy(indicatedTo, item =>
            item.type === 'entity' ? 'entityIds' : 'readerIds'
          )
        : {}

      const params = {
        contentIdentifier: book.slug || book.id?.toString() || '',
        ...indicateTo
      }
      return params
    })

    const targets: Target[] = indicatedTo.map(item => ({
      targetId: item.id,
      targetType:
        item.type === 'entity'
          ? IndicationTargetType.ENTITY
          : IndicationTargetType.USER
    }))

    const promises = indications.map(indication => {
      if (isVideoIndication) {
        return customInstance('/api-arvore/videos/indicate', {
          method: 'POST',
          body: JSON.stringify({
            videoSlug: indication.contentIdentifier,
            dueDate: formatModalDate(hasDate),
            targets: targets.map(target => ({
              targetId: target.targetId,
              targetType:
                target.targetType === IndicationTargetType.ENTITY
                  ? 'entity'
                  : 'user'
            }))
          })
        })
      }

      const input = {
        contentIdentifier: indication.contentIdentifier,
        contentType: props.contentType,
        dueDate: formatModalDate(hasDate),
        targets
      }
      return client.mutate({
        mutation: createContentIndications,
        variables: { input }
      })
    })

    Promise.all(promises)
      .then(() => {
        props.onCompleteIndication()
        toastMessage({
          message: 'Indicação concluída com sucesso.',
          type: 'success'
        })
      })
      .catch(err => {
        setState({ loading: false })
        toastMessage({
          message: t(`Oops, aconteceu um erro: {{error}}`, {
            error: err.message
          }),
          type: 'error'
        })
      })
  }

  const handleSubmit = (e, indicatedBooks: ContentIndicationItem[]) => {
    e.preventDefault()

    setState({
      loading: true
    })

    submitIndication(indicatedBooks, user.isTeacher)
  }

  const handleBookAutocompleteItemClick = (item: Book) => {
    client
      .query({
        query: bookQuery,
        variables: {
          slug: item.slug
        }
      })
      .then(response => {
        const book = response.data.bookDetails
        setState({
          autocompleteBooks: [],
          books: state.books.concat({
            slug: book.slug,
            name: book.name,
            author: book.author,
            imageUrlThumb: book.imageUrlThumb
          })
        })
        bookInputRef.current = ''
      })
      .catch(err => {
        toastMessage({
          message: t(`Oops, aconteceu um erro: {{error}}`, {
            error: err.message
          }),
          type: 'error'
        })
      })
  }

  const bookSearch = () => {
    if (!bookInputRef.current) {
      setState({ autocompleteBooks: [] })
      return
    }

    client
      .query<{
        searchBook: Book[]
      }>({
        query: searchBookForAutoCompleteQuery,
        variables: { searchTerm: bookInputRef.current }
      })
      .then(response => {
        const autocompleteBooks = response.data.searchBook
          .slice(0, ITEMS_TO_SHOW_IN_AUTOCOMPLETE)
          .map(book => ({
            name: book.name,
            slug: book.slug,
            type: 'book'
          }))

        setState({ autocompleteBooks })
      })
      .catch(err => {
        toastMessage({
          message: t(`Oops, aconteceu um erro: {{error}}`, {
            error: err.message
          }),
          type: 'error'
        })
      })
  }

  const handleIndicatedToRemoval = (e: React.MouseEvent, id: number) => {
    e.preventDefault()
    setState({
      indicatedTo: state.indicatedTo.filter(i => i.id !== id)
    })
  }

  const handleIndicationAutocompleteItemClick = (item: {
    id: number
    name: string
    type: string
  }) => {
    if (!user.isTeacher) {
      setState({
        autocompleteIndicatedTo: [],
        indicatedTo: [
          {
            id: item.id,
            name: item.name,
            type: item.type
          }
        ]
      })
      indicateToRef.current = ''
    }

    setState({
      autocompleteIndicatedTo: [],
      indicatedTo: state.indicatedTo.concat({
        id: item.id,
        name: item.name,
        type: item.type
      })
    })
    indicateToRef.current = ''
  }

  const filterIndicatedTo = (userList, userId, isTeacher) => {
    return userList
      .filter(reader => parseInt(reader.id) !== parseInt(userId))
      .map(item => {
        return {
          id: isTeacher ? item.user_id : item.user.id,
          name: isTeacher
            ? `${item.name} - ${item.entity_name}`
            : `${item.user.name} - ${item.entity.name}`,
          type: 'reader'
        }
      })
      .filter(item => {
        for (let i = 0; i < state.indicatedTo.length; i++) {
          if (state.indicatedTo[i].id === item.id) return false
        }
        return true
      })
      .slice(0, 15)
  }

  const indicatedToSearch = async () => {
    const isTeacher = user.isTeacher
    if (!indicateToRef.current) {
      setState({ autocompleteIndicatedTo: [] })
      return
    }

    try {
      if (isTeacher) {
        const entitiesMap = new Map(
          state.entitiesSubtreeFlat.map(e => [String(e.id), e])
        )

        const getAncestryLabel = (
          entity: typeof state.entitiesSubtreeFlat[0]
        ) => {
          if (entity.entity_type !== 'class' || !entity.ancestry)
            return undefined

          const ancestorIds = entity.ancestry.split('/')
          const ancestorNames = ancestorIds
            .map(id => entitiesMap.get(id)?.name)
            .filter(Boolean)

          return ancestorNames.length ? ancestorNames.join(' > ') : undefined
        }

        let autocompleteIndicatedTo = state.entitiesSubtreeFlat
          .filter(item => {
            const searchTerm = indicateToRef.current
            const escapedTerm = searchTerm.replace(
              /[.*+?^${}()|[\]\\]/g,
              '\\$&'
            )
            const regex = new RegExp(escapedTerm, 'i')
            const ancestryLabel = getAncestryLabel(item)
            const ancestryWithoutNumbers = ancestryLabel
              ? ancestryLabel.replace(/\d+/g, '').trim()
              : ''
            return regex.test(item.name) || regex.test(ancestryWithoutNumbers)
          })
          .slice(0, 15)
          .map(item => ({
            ...item,
            superscript: getAncestryLabel(item)
          }))

        const res = await client.query({
          query: searchAvailableReadersToIndicateQuery,
          variables: {
            term: indicateToRef.current
          }
        })

        const { searchAvailableReadersToIndicate } = res.data.me.teacher

        autocompleteIndicatedTo = autocompleteIndicatedTo.concat(
          filterIndicatedTo(
            searchAvailableReadersToIndicate,
            user.reader.id,
            isTeacher
          )
        )
        setState({ autocompleteIndicatedTo })
      } else {
        const res = await client.query({
          query: listReadersWithSameDegreeQuery,
          variables: {
            term: indicateToRef.current
          }
        })

        const { listReadersSameDegree } = res.data.me.reader

        const autocompleteIndicatedTo = filterIndicatedTo(
          listReadersSameDegree,
          user.reader.id,
          isTeacher
        )
        setState({ autocompleteIndicatedTo })
      }
    } catch (err) {
      if (err?.networkError?.name === 'AbortError') {
        return
      }

      toastMessage({
        message: t(`Oops, aconteceu um erro: {{error}}`, {
          error: err.message
        }),
        type: 'error'
      })
    }
  }

  useEffect(() => {
    if (user.isTeacher) {
      client
        .query<{
          me: User
        }>({
          query: entitiesSubtreeFlatQuery
        })
        .then(res => {
          const { entitiesSubtreeFlat } = res.data.me.teacher

          setState({
            entitiesSubtreeFlat: entitiesSubtreeFlat.map(entity => ({
              type: 'entity',
              ...entity
            }))
          })
        })
        .catch(err =>
          toastMessage({
            message: t(`Oops, aconteceu um erro: {{error}}`, {
              error: err.message
            }),
            type: 'error'
          })
        )
    }
  }, [client, setState, t, toastMessage, user.isTeacher])

  const { loading, dateIsValid, dateHelper, indicatedTo } = state
  const indicatedBooks = props.preselectedBook
    ? [props.preselectedBook]
    : state.books

  return (
    <Form>
      <Field>
        {!props.preselectedBook && (
          <Fragment>
            <Input
              id="book"
              aria-label={t('Livro para indicar:')}
              name="book"
              label={t('Livro para indicar:')}
              onChange={e => {
                bookInputRef.current = e.target.value
              }}
              onKeyUp={debounce(bookSearch, 250)}
              autoComplete="off"
            />
            <StyledAutoComplete
              visible={state.autocompleteBooks.length > 0}
              data={state.autocompleteBooks}
              term={bookInputRef.current ? bookInputRef.current : ''}
              onClickItem={handleBookAutocompleteItemClick}
            />
          </Fragment>
        )}
        <List>
          {indicatedBooks?.map(book => (
            <ListBook
              key={book?.slug}
              title={book?.name}
              subtitle={book?.author}
              contentFormat={
                isVideoIndication || !book?.imageUrlThumb ? 'newscover' : 'book'
              }
              coverThumb={
                book?.imageUrlThumb || (book as ContentIndicationItem).image
              }
              hideButton
            />
          ))}
        </List>
      </Field>
      <Field>
        <Input
          id="indicate-to"
          name="indicate-to"
          aria-label={t('Indicar para:')}
          label={t('Indicar para:')}
          helpText={user.isTeacher && t('Pesquise por alunos e turmas')}
          onChange={e => {
            indicateToRef.current = e.target.value
          }}
          onKeyUp={debounce(indicatedToSearch, 250)}
          autoComplete="off"
        />
        <StyledAutoComplete
          visible={state.autocompleteIndicatedTo.length > 0}
          data={state.autocompleteIndicatedTo}
          term={indicateToRef.current ? indicateToRef.current : ''}
          onClickItem={handleIndicationAutocompleteItemClick}
        />
        <List>
          {state.indicatedTo.map(indicatedTo => (
            <Item key={indicatedTo.id}>
              <IndicatedTo>
                <span>{indicatedTo.name}</span>
                <Link
                  onClick={e => handleIndicatedToRemoval(e, indicatedTo.id)}
                >
                  {t('Remover')}
                </Link>
              </IndicatedTo>
            </Item>
          ))}
        </List>
      </Field>
      {user.isTeacher && hasActivitiesV3 && (
        <>
          <Field>
            <SwitchWrapper>
              <ToggleSwitch
                active={showDeadline}
                aria-label={t('Adicionar prazo')}
                onChange={() => setShowDeadline(!showDeadline)}
              />
              <span>{t('Adicionar prazo')}</span>
              <FontIcon
                iconName="information"
                size={18}
                data-tip={t(
                  'Ao adicionar um prazo, a indicação aparecerá para o aluno na página Atividades.'
                )}
                data-for="deadline-tooltip"
                style={{ cursor: 'help' }}
              />
              <ReactTooltip id="deadline-tooltip" place="top" effect="solid" />
            </SwitchWrapper>
          </Field>
          {showDeadline && (
            <>
              <Field>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  label={t('Ler até:')}
                  onChange={e => {
                    dateInputRef.current = e.target.value
                  }}
                  helpText={!dateIsValid ? dateHelper : null}
                />
              </Field>
              <InfoAlertWrapper>
                <InfoAlert
                  title={t(
                    'Indicações com prazo aparecem como atividades para os alunos e também aparecerão para o professor no relatório de atividades'
                  )}
                  icon="information"
                  boldTitle
                  variant="info"
                  size={16}
                  infoAlertSize="small"
                />
              </InfoAlertWrapper>
            </>
          )}
        </>
      )}
      {user.isTeacher && !hasActivitiesV3 && (
        <Field>
          <Input
            id="date"
            name="date"
            type="date"
            label={t('Ler até:')}
            onChange={e => {
              dateInputRef.current = e.target.value
            }}
            helpText={!dateIsValid ? dateHelper : null}
          />
        </Field>
      )}
      <Button
        onClick={event => handleSubmit(event, indicatedBooks)}
        theme={{
          type: 'primaryLivros',
          size: 'xLarge'
        }}
        fluid
        text={t('Indicar')}
        loading={loading}
        disabled={!indicatedTo.length || !indicatedBooks.length}
      />
    </Form>
  )
}

export default ContentIndicationForm
